/**
 * EditCalendarModal — modal form for upserting one (organization, academicYear)
 * calendar.
 *
 * Six scalar inputs (student/teacher start/end/total days) and one
 * react-day-picker per SpecialDayType (Inservice, Clerical, Professional
 * Development, Closed, Graduation). Weekends are disabled for all types
 * except Graduation, per the rule in calendarYearUtils.ts.
 *
 * The modal does not call the API itself — it returns the prepared payload
 * via onSave so the parent (CalendarTab) owns the loading/error state and
 * the activity-log triggering refresh.
 */

import { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Label, TextInput, Alert } from 'flowbite-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
// Pull in our shared overrides (dark-mode tweaks + cmg-big styles).
// CSS imports are global, so importing here ensures the modal pickers are
// dark-mode-correct even before the user has opened the Calendar tab.
import './CalendarMonthGrid.css';

import type { Descriptor } from '../../api/descriptors';
import type { CalendarResponse, CalendarUpsert } from '../../api/calendar';
import {
  parseAcademicYearCode,
  toIsoDate,
  fromIsoDate,
  WEEKEND_ALLOWED_SPECIAL_DAY_CODES,
} from './calendarYearUtils';
import { TYPE_LABELS, TYPE_SWATCHES } from './CalendarMonthGrid';

interface EditCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the prepared upsert payload. Parent sends to API. */
  onSave: (payload: CalendarUpsert) => Promise<void>;
  /** Existing calendar (or null if creating fresh for this year). */
  calendar: CalendarResponse | null;
  /** Selected academic year code, e.g. "2025-2026". */
  academicYearCode: string;
  /** All SpecialDayType descriptors, ordered for display. */
  specialDayTypes: Descriptor[];
  organizationName: string;
}

type DateSetByType = Record<number, Set<string>>; // descriptorId -> set of ISO date strings

function buildInitialDates(
  calendar: CalendarResponse | null,
  specialDayTypes: Descriptor[]
): DateSetByType {
  const result: DateSetByType = {};
  for (const t of specialDayTypes) {
    result[t.descriptorId] = new Set<string>();
  }
  if (calendar) {
    for (const day of calendar.days) {
      const set = result[day.specialDayTypeDescriptorId];
      if (set) set.add(day.calendarDate);
    }
  }
  return result;
}

export const EditCalendarModal: React.FC<EditCalendarModalProps> = ({
  isOpen,
  onClose,
  onSave,
  calendar,
  academicYearCode,
  specialDayTypes,
  organizationName,
}) => {
  const yearBounds = useMemo(() => parseAcademicYearCode(academicYearCode), [academicYearCode]);

  const [studentStartDate, setStudentStartDate] = useState<string>('');
  const [studentEndDate, setStudentEndDate] = useState<string>('');
  const [studentTotalDays, setStudentTotalDays] = useState<string>('');
  const [teacherStartDate, setTeacherStartDate] = useState<string>('');
  const [teacherEndDate, setTeacherEndDate] = useState<string>('');
  const [teacherTotalDays, setTeacherTotalDays] = useState<string>('');
  const [datesByType, setDatesByType] = useState<DateSetByType>({});

  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Reset form whenever the modal opens or the underlying calendar changes
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setIsSaving(false);
    setStudentStartDate(calendar?.studentStartDate ?? '');
    setStudentEndDate(calendar?.studentEndDate ?? '');
    setStudentTotalDays(
      calendar?.studentTotalDays != null ? String(calendar.studentTotalDays) : ''
    );
    setTeacherStartDate(calendar?.teacherStartDate ?? '');
    setTeacherEndDate(calendar?.teacherEndDate ?? '');
    setTeacherTotalDays(
      calendar?.teacherTotalDays != null ? String(calendar.teacherTotalDays) : ''
    );
    setDatesByType(buildInitialDates(calendar, specialDayTypes));
  }, [isOpen, calendar, specialDayTypes]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!yearBounds) {
      setError(`'${academicYearCode}' is not a valid academic year code.`);
      return;
    }
    setError(null);
    setIsSaving(true);

    const days = Object.entries(datesByType).flatMap(([descriptorId, set]) =>
      Array.from(set).map((calendarDate) => ({
        specialDayTypeDescriptorId: Number(descriptorId),
        calendarDate,
      }))
    );

    const payload: CalendarUpsert = {
      studentStartDate: studentStartDate || null,
      studentEndDate: studentEndDate || null,
      studentTotalDays: studentTotalDays ? Number(studentTotalDays) : null,
      teacherStartDate: teacherStartDate || null,
      teacherEndDate: teacherEndDate || null,
      teacherTotalDays: teacherTotalDays ? Number(teacherTotalDays) : null,
      days,
    };

    try {
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save calendar.');
    } finally {
      setIsSaving(false);
    }
  };

  const totalSelected = useMemo(
    () => Object.values(datesByType).reduce((sum, set) => sum + set.size, 0),
    [datesByType]
  );

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="4xl" popup>
      <div className="flex flex-col max-h-[90vh] rounded-lg overflow-hidden">
        {/* Sticky header */}
        <div className="px-6 pt-6 pb-3 shrink-0 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Edit Calendar — {academicYearCode}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {organizationName}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 min-h-0"
        >
          {/* Scrollable body. Inner wrapper is constrained to the picker
              row's natural width (720px = 2 × 352px cards + 16px gap), so
              both the top fields and the picker section share the same
              horizontal extent and are centered within the modal — natural
              even margins on each side without forcing justify-center. */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="mx-auto space-y-6" style={{ maxWidth: 720 }}>
          {error && (
            <Alert color="failure" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* --- Student / teacher day bounds --- */}
          <fieldset className="space-y-4">
            <legend className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Student & teacher day bounds
            </legend>
            {/* 2 rows × 3 cols, each cell the same width. At the modal's
                4xl content width (~848px), each field renders ~272px wide.
                Comfortable for both date and 3-digit number inputs. */}
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
              <div>
                <Label htmlFor="studentStartDate">First student day</Label>
                <TextInput
                  id="studentStartDate"
                  type="date"
                  value={studentStartDate}
                  onChange={(e) => setStudentStartDate(e.target.value)}
                  min={yearBounds ? toIsoDate(yearBounds.fromDate) : undefined}
                  max={yearBounds ? toIsoDate(yearBounds.toDate) : undefined}
                />
              </div>
              <div>
                <Label htmlFor="studentEndDate">Last student day</Label>
                <TextInput
                  id="studentEndDate"
                  type="date"
                  value={studentEndDate}
                  onChange={(e) => setStudentEndDate(e.target.value)}
                  min={yearBounds ? toIsoDate(yearBounds.fromDate) : undefined}
                  max={yearBounds ? toIsoDate(yearBounds.toDate) : undefined}
                />
              </div>
              <div>
                <Label htmlFor="studentTotalDays">Total student days</Label>
                <TextInput
                  id="studentTotalDays"
                  type="number"
                  min={0}
                  max={366}
                  value={studentTotalDays}
                  onChange={(e) => setStudentTotalDays(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="teacherStartDate">First teacher day</Label>
                <TextInput
                  id="teacherStartDate"
                  type="date"
                  value={teacherStartDate}
                  onChange={(e) => setTeacherStartDate(e.target.value)}
                  min={yearBounds ? toIsoDate(yearBounds.fromDate) : undefined}
                  max={yearBounds ? toIsoDate(yearBounds.toDate) : undefined}
                />
              </div>
              <div>
                <Label htmlFor="teacherEndDate">Last teacher day</Label>
                <TextInput
                  id="teacherEndDate"
                  type="date"
                  value={teacherEndDate}
                  onChange={(e) => setTeacherEndDate(e.target.value)}
                  min={yearBounds ? toIsoDate(yearBounds.fromDate) : undefined}
                  max={yearBounds ? toIsoDate(yearBounds.toDate) : undefined}
                />
              </div>
              <div>
                <Label htmlFor="teacherTotalDays">Total teacher days</Label>
                <TextInput
                  id="teacherTotalDays"
                  type="number"
                  min={0}
                  max={366}
                  value={teacherTotalDays}
                  onChange={(e) => setTeacherTotalDays(e.target.value)}
                />
              </div>
            </div>
          </fieldset>

          {/* --- Special days (one picker per type) --- */}
          <fieldset className="space-y-4">
            <legend className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Special days{' '}
              <span className="font-normal text-gray-500 dark:text-gray-400">
                ({totalSelected} selected total)
              </span>
            </legend>

            {/* Flex-wrap layout with fixed-width cards. Cards left-align
                so partial last rows look intentional rather than
                "centered with awkward whitespace either side". */}
            <div className="flex flex-wrap gap-4 justify-start">
              {specialDayTypes.map((type) => {
                const allowWeekends = WEEKEND_ALLOWED_SPECIAL_DAY_CODES.has(
                  type.codeValue
                );
                const isoSet = datesByType[type.descriptorId] ?? new Set<string>();
                const selectedDates = Array.from(isoSet).map(fromIsoDate);

                const disabledMatchers: any[] = [];
                if (yearBounds) {
                  disabledMatchers.push({ before: yearBounds.fromDate });
                  disabledMatchers.push({ after: yearBounds.toDate });
                }
                if (!allowWeekends) {
                  disabledMatchers.push({ dayOfWeek: [0, 6] });
                }

                const swatchClass = TYPE_SWATCHES[type.codeValue] ?? 'bg-gray-400';
                const typeLabel =
                  TYPE_LABELS[type.codeValue] ?? type.shortDescription;

                return (
                  <div
                    key={type.descriptorId}
                    className="rounded-lg border border-gray-200 dark:border-gray-600 p-4 w-[22rem]"
                  >
                    {/* Type title — given more visual weight than the
                        picker's "July 2026" caption so the eye lands on
                        what matters (the day type) first. */}
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100 dark:border-gray-700">
                      <span
                        className={`inline-block h-3 w-3 rounded-sm shrink-0 ${swatchClass}`}
                        aria-hidden="true"
                      />
                      <h4 className="text-base font-bold text-gray-900 dark:text-gray-100">
                        {typeLabel}
                      </h4>
                      <span className="ml-auto text-xs text-gray-500 dark:text-gray-400 shrink-0 whitespace-nowrap">
                        {isoSet.size} {isoSet.size === 1 ? 'date' : 'dates'}
                        {allowWeekends ? ' (weekends ok)' : ''}
                      </span>
                    </div>
                    <DayPicker
                      mode="multiple"
                      selected={selectedDates}
                      onSelect={(dates) => {
                        const newSet = new Set<string>(
                          (dates ?? []).map((d) => toIsoDate(d))
                        );
                        setDatesByType((prev) => ({
                          ...prev,
                          [type.descriptorId]: newSet,
                        }));
                      }}
                      startMonth={yearBounds?.fromDate}
                      endMonth={yearBounds?.toDate}
                      disabled={disabledMatchers}
                      defaultMonth={yearBounds?.fromDate}
                    />
                  </div>
                );
              })}
            </div>
          </fieldset>

          </div>
          </div>

          {/* Sticky footer */}
          <div className="shrink-0 flex justify-end gap-2 px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <Button color="light" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Calendar'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
